import { contrastRatio } from '../app-ui-settings/validate.js';
import { TOKENS_COLOR_DEFAULT, TOKENS_SIZE_DEFAULT } from './tokens-default.generated.js';
export const TOKEN_SIZE_GROUPS = ['fontSize', 'radius', 'spacing', 'dimension'];
export const TOKEN_SIZE_LIMITS = {
    fontSize: { min: 6, max: 64 },
    radius: { min: 0, max: 9999 },
    spacing: { min: 0, max: 200 },
    dimension: { min: 0, max: 1000 },
};
export const TOKEN_NAME_PATTERN = /^[\da-z][\da-z-]{0,39}$/;
const isTokenSizeGroup = (value) => TOKEN_SIZE_GROUPS.includes(value);
const isSizeValue = (group, value) => typeof value === 'number' &&
    Number.isFinite(value) &&
    value >= TOKEN_SIZE_LIMITS[group].min &&
    value <= TOKEN_SIZE_LIMITS[group].max;
export const TOKEN_COLOR_MODES = ['light', 'dark'];
const isTokenColorMode = (value) => TOKEN_COLOR_MODES.includes(value);
export const TOKENS_DEFAULT_PAYLOAD = {
    color: TOKENS_COLOR_DEFAULT,
    size: TOKENS_SIZE_DEFAULT,
};
const HEX_COLOR = /^#[\dA-F]{6}$/;
const REFERENCE = /^{([\da-z-]+(?:\.[\da-z-]+)*)}$/;
const isTree = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
export function flattenTokenTree(tree, prefix = '') {
    const result = new Map();
    for (const [key, value] of Object.entries(tree)) {
        const path = prefix ? `${prefix}.${key}` : key;
        if (typeof value === 'string')
            result.set(path, value);
        else
            for (const [childPath, childValue] of flattenTokenTree(value, path))
                result.set(childPath, childValue);
    }
    return result;
}
export function unflattenTokenTree(values) {
    const root = {};
    for (const [path, value] of values) {
        const parts = path.split('.');
        const leaf = parts.pop() ?? '';
        let node = root;
        for (const part of parts) {
            const child = node[part];
            if (typeof child === 'object')
                node = child;
            else {
                const created = {};
                node[part] = created;
                node = created;
            }
        }
        node[leaf] = value;
    }
    return root;
}
export function resolveTokenColor(values, path) {
    const seen = new Set();
    let current = path;
    for (;;) {
        if (seen.has(current))
            return null;
        seen.add(current);
        const value = values.get(current);
        if (value === undefined)
            return null;
        const reference = REFERENCE.exec(value);
        if (!reference)
            return value;
        current = reference[1] ?? '';
    }
}
function leavesOf(tree, prefix = '') {
    const result = new Map();
    for (const [key, value] of Object.entries(tree)) {
        const path = prefix ? `${prefix}.${key}` : key;
        if (isTree(value))
            for (const [childPath, childValue] of leavesOf(value, path))
                result.set(childPath, childValue);
        else
            result.set(path, value);
    }
    return result;
}
export const TOKEN_BACKGROUND_MIN_CONTRAST = 4.5;
const BACKGROUND_TOKENS = ['background.neutral.primary', 'background.neutral.secondary'];
const MAIN_TEXT_TOKEN = 'text.neutral.primary';
const isTokenValue = (value) => typeof value === 'string' && (HEX_COLOR.test(value) || REFERENCE.test(value));
export function validateTokensPayload(payload) {
    const errors = [];
    const warnings = [];
    if (!isTree(payload))
        return { ok: false, errors: ['tokens: must be an object'] };
    for (const key of Object.keys(payload))
        if (key !== 'color' && key !== 'size')
            errors.push(`${key}: unknown field`);
    const size = payload['size'];
    if (size !== undefined && !isTree(size))
        errors.push('size: must be an object');
    if (isTree(size)) {
        for (const [group, values] of Object.entries(size)) {
            if (!isTokenSizeGroup(group)) {
                errors.push(`size.${group}: unknown group`);
                continue;
            }
            if (!isTree(values)) {
                errors.push(`size.${group}: must be an object`);
                continue;
            }
            const { min, max } = TOKEN_SIZE_LIMITS[group];
            for (const [name, value] of Object.entries(values)) {
                if (!TOKEN_NAME_PATTERN.test(name))
                    errors.push(`size.${group}.${name}: token names are lowercase letters, digits and - (up to 40)`);
                else if (!isSizeValue(group, value))
                    errors.push(`size.${group}.${name}: must be a number from ${min} to ${max}`);
            }
        }
    }
    const color = payload['color'];
    if (color !== undefined && !isTree(color))
        errors.push('color: must be an object');
    if (isTree(color)) {
        for (const key of Object.keys(color))
            if (!isTokenColorMode(key))
                errors.push(`color.${key}: unknown theme`);
        for (const mode of TOKEN_COLOR_MODES) {
            const tree = color[mode];
            if (tree === undefined)
                continue;
            if (!isTree(tree)) {
                errors.push(`color.${mode}: must be an object`);
                continue;
            }
            const known = flattenTokenTree(TOKENS_COLOR_DEFAULT[mode]);
            const given = new Map();
            const walk = (node, prefix) => {
                for (const [key, value] of Object.entries(node)) {
                    const path = prefix ? `${prefix}.${key}` : key;
                    if (isTree(value))
                        walk(value, path);
                    else if (!known.has(path))
                        errors.push(`color.${mode}.${path}: unknown token`);
                    else if (!isTokenValue(value))
                        errors.push(`color.${mode}.${path}: must be #RRGGBB or {token}`);
                    else
                        given.set(path, value);
                }
            };
            walk(tree, '');
            const merged = new Map([...known, ...given]);
            for (const path of given.keys())
                if (resolveTokenColor(merged, path) === null)
                    errors.push(`color.${mode}.${path}: reference does not resolve (missing token or loop)`);
            const text = resolveTokenColor(merged, MAIN_TEXT_TOKEN);
            for (const path of BACKGROUND_TOKENS) {
                const background = resolveTokenColor(merged, path);
                if (!text || !background)
                    continue;
                if (!given.has(path) && !given.has(MAIN_TEXT_TOKEN))
                    continue;
                const ratio = contrastRatio(background, text);
                if (ratio < TOKEN_BACKGROUND_MIN_CONTRAST)
                    warnings.push(`color.${mode}.${path}: low contrast with ${MAIN_TEXT_TOKEN} (${text}) — ${ratio.toFixed(1)}:1, recommended ${TOKEN_BACKGROUND_MIN_CONTRAST}:1 or more`);
            }
        }
    }
    return errors.length
        ? { ok: false, errors, warnings }
        : { ok: true, payload: payload, errors, warnings };
}
export function mergeTokensPayload(builtIn, received) {
    if (!isTree(received))
        return null;
    const receivedColor = isTree(received['color']) ? received['color'] : {};
    const color = {};
    for (const mode of TOKEN_COLOR_MODES) {
        const base = flattenTokenTree(TOKENS_COLOR_DEFAULT[mode]);
        for (const [path, value] of leavesOf(builtIn.color?.[mode] ?? {}))
            if (base.has(path) && isTokenValue(value))
                base.set(path, value);
        const tree = receivedColor[mode];
        if (isTree(tree)) {
            for (const [path, value] of leavesOf(tree))
                if (base.has(path) && isTokenValue(value))
                    base.set(path, value);
        }
        for (const path of base.keys())
            if (resolveTokenColor(base, path) === null)
                return null;
        color[mode] = unflattenTokenTree(base);
    }
    const receivedSize = isTree(received['size']) ? received['size'] : {};
    const size = {
        fontSize: {},
        radius: {},
        spacing: {},
        dimension: {},
    };
    for (const group of TOKEN_SIZE_GROUPS) {
        const values = { ...TOKENS_SIZE_DEFAULT[group] };
        for (const layer of [builtIn.size?.[group], receivedSize[group]]) {
            if (!isTree(layer))
                continue;
            for (const [name, value] of Object.entries(layer))
                if (TOKEN_NAME_PATTERN.test(name) && isSizeValue(group, value))
                    values[name] = value;
        }
        size[group] = values;
    }
    return { color, size };
}
//# sourceMappingURL=tokens.js.map